import { useNavigate } from 'react-router-dom'

import { Box, Button, ColumnSplit, Text } from '@factorialco/deprecated-design-system'

import Loading from 'modules/core/components/Loading'
import type { PanesHeaderInlineInfoProps } from 'modules/core/components/PanesHeader'
import PanesHeader from 'modules/core/components/PanesHeader'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { questionGroupRespondent } from 'modules/core/store/collections/users'

import { employeeReferenceContract } from 'modules/contracts/lib/utils'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import AnonymousBanner from 'modules/surveys/components/AnonymousBanner'
import UserBlock from 'modules/surveys/components/UserBlock'
import BlankSlate from 'modules/surveys/components/WriteResponse/BlankSlate'
import ResponseQuestions from 'modules/surveys/components/WriteResponse/ResponseQuestions'
import ResponseValues from 'modules/surveys/components/WriteResponse/ResponseValues'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'
import type Survey from 'modules/surveys/store/models/v2/Survey'

interface QuestionsProps {
  questionGroup: QuestionGroup
  onSubmit: () => void
  onDraftSaved: () => Promise<void>
  canReview?: boolean
  form: SurveyForm | Survey
  contractVersions: ContractVersions
}

function Questions({
  questionGroup,
  onSubmit,
  onDraftSaved,
  canReview = true,
  form,
  contractVersions,
}: QuestionsProps) {
  if (!questionGroup.attributes.questions.length) {
    return <BlankSlate />
  }

  if (questionGroup.isAnswered || !form.isActive) {
    return <ResponseValues questionGroup={questionGroup} />
  }

  return (
    <ResponseQuestions
      canReview={canReview}
      onSubmit={onSubmit}
      onDraftSaved={onDraftSaved}
      questionGroup={questionGroup}
    />
  )
}

interface Props {
  loading: boolean
  form: SurveyForm | Survey
  questionGroup: QuestionGroup
  onDraftSaved: () => Promise<void>
  canEditSurvey: boolean
  contractVersions: ContractVersions
}

export default function SurveyAnswer({
  loading,
  form,
  questionGroup,
  onDraftSaved,
  canEditSurvey,
  contractVersions,
}: Props) {
  const { employee: currentEmployee, access: currentAccess } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const user = questionGroupRespondent(questionGroup)
  const isAnsweredOrClosed = questionGroup?.isAnswered || questionGroup?.isClosed
  const employee = user && user.employee
  const jobTitle = employee
    ? employeeReferenceContract(contractVersions, employee)?.attributes.job_title
    : null
  const canReview = currentAccess.id === user?.id
  const hasWelcomeSpaceAccess =
    !!currentEmployee && !hasRegularAccess(currentEmployee.hasRegularAccess)

  const onSubmit = () => {
    if (hasWelcomeSpaceAccess) {
      return navigate(navigation.employee_onboarding.onboardingTasks.fullPath())
    }

    if (canEditSurvey) {
      return navigate(
        navigation.engagement.surveys.id.configuration.fullPath({ id: form.attributes.id })
      )
    }

    return navigate(navigation.engagement.fullPath())
  }

  const inlineInfo = (): PanesHeaderInlineInfoProps[] => {
    const data: PanesHeaderInlineInfoProps[] = []

    if (!form.isAnonymous) {
      data.push({
        icon: 'manager',
        message: i18n.tp('surveys.answer.participants', {
          count: form.attributes.question_groups_count,
        }),
      })
    }

    if (form.isAnonymous) {
      data.push({
        icon: 'lock-locked',
        message: i18n.t('surveys.answer.anonymous'),
      })
    } else {
      data.push({
        icon: 'eye-visible',
        message: i18n.t('surveys.answer.visible'),
      })
    }

    if (questionGroup.isDraft) {
      const draftedAtDate = stringToDate(questionGroup.attributes.drafted_at, 'backend_datetime')
      data.push({
        icon: 'clock',
        message: `${i18n.t('surveys.answer.last_updated')} ${dateToString(
          draftedAtDate,
          'ui_short_with_time'
        )}`,
      })
    }

    return data
  }

  if (loading) return <Loading />

  return (
    <Box flexDirection="column" width="full">
      {form.isAnonymous && <AnonymousBanner />}
      <ColumnSplit colored={false} minWidth="none">
        <>
          <PanesHeader
            illustration="surveys"
            title={form.attributes.name}
            description={
              <Text.Normal htmlTag="p" whiteSpace="breakSpaces">
                {form.attributes.description || i18n.t('surveys.answer.description')}
              </Text.Normal>
            }
            inlineInfo={inlineInfo()}
          />
          {isAnsweredOrClosed && (
            <Button.Router
              to={navigation.engagement.surveys.id.responses.fullPath({ id: form.attributes.id })}
              type="secondary"
            >
              {i18n.t('surveys.form.responses.title')}
            </Button.Router>
          )}
        </>
        <Box>
          {user && (
            <UserBlock user={form.isAnonymous ? null : user} description={jobTitle}>
              <Questions
                canReview={canReview}
                onSubmit={onSubmit}
                onDraftSaved={onDraftSaved}
                questionGroup={questionGroup}
                form={form}
                contractVersions={contractVersions}
              />
            </UserBlock>
          )}
        </Box>
      </ColumnSplit>
    </Box>
  )
}
